// apps/web/src/widgets/template-renderers.ts
//
// Phase 6 D-11 dispatcher registry. This Map is the single source of truth
// for template-id ↔ renderer-FC mapping. Phase 7 D-17 / D-18 wraps it in
// Object.freeze as Layer 3 of the 4-layer WIDG-10 defense.
//
// Adding a new template (per docs/ADD-A-TEMPLATE.md Plan 12):
//   1. Add the templateId literal to packages/shared/src/schemas/widget-templates-catalog.ts
//   2. Add the typed configSchema under packages/shared/src/schemas/widget-templates/
//   3. Extend ConfigForTemplate<K> + StateForTemplate<K> in ./config-for-template.ts
//   4. Write the renderer in apps/web/src/widgets/<templateId>/renderer.tsx
//   5. Add a Map entry HERE — that's the entire WIDG-08 surface.
//
// Phase 7 D-17 four-layer WIDG-10 defense:
//   Layer 1 (compile-time):  `ReadonlyMap<TemplateId, React.FC<unknown>>` type erases .set / .delete
//   Layer 2 (runtime throw): `registerExternalTemplate()` throws `WidgetRegistryClosedError` (./registry-lockdown.ts)
//   Layer 3 (runtime freeze): `Object.freeze(templateRenderers)` — THIS FILE
//   Layer 4 (static analysis): `scripts/check-registry-closed.sh` grep gate against `.set(` / `.delete(`
//
// Critical correctness note (RESEARCH §"Pitfall 2"): Object.freeze on a Map does
// NOT block .set() or .delete() because Map internals live in slots, not own
// properties. The ReadonlyMap type erasure (Layer 1) blocks at compile time;
// the grep gate (Layer 4) blocks at static-analysis time. Object.freeze is
// defense-in-depth — it freezes the OBJECT shell, prevents adding own
// properties, and signals intent.
//
// Note on type erasure: Map value type is React.FC<unknown> because TS cannot
// store heterogeneous-generic-narrowed FCs in a single Map signature. The
// dispatcher (template-renderer.tsx) casts back at the call site using
// WidgetRendererProps<K>. The type-map in config-for-template.ts is what
// enforces correctness statically.

import type { TemplateId } from "@the-recipe-spread/shared";
import type * as React from "react";
import { BranchVariantRenderer } from "./branch-variant/renderer.js";
import { FreezePickerRenderer } from "./freeze-picker/renderer.js";
import { OptionSetRenderer } from "./option-set/renderer.js";
import { OptionTierMatrixRenderer } from "./option-tier-matrix/renderer.js";
import { PerOptionDetailCardRenderer } from "./per-option-detail-card/renderer.js";
import { RankedTierRenderer } from "./ranked-tier/renderer.js";
import { ScalingMatrixRenderer } from "./scaling-matrix/renderer.js";
import { ShoppingListAggregatorRenderer } from "./shopping-list-aggregator/renderer.js";
import { SizeAndYieldRenderer } from "./size-and-yield/renderer.js";
import { StaticNoteRenderer } from "./static-note/renderer.js";
import { SummaryChecklistRenderer } from "./summary-checklist/renderer.js";

// Registered template renderers — every cataloged template now has a renderer.
// "scaling-matrix" joined here in Phase 11.4; "option-tier-matrix" +
// "per-option-detail-card" join here in Phase 11.5; "freeze-picker" joins here in
// Phase 11.6 Plan 05; the final cataloged template "shopping-list-aggregator"
// joins here in Phase 11.8 Plan 04 (step 5 of the add-a-template procedure — its
// schema-side steps 1-4 predate Phase 11). UNRENDERED_TEMPLATE_IDS is now empty.
const _renderers = new Map<TemplateId, React.FC<unknown>>([
  ["option-set", OptionSetRenderer as unknown as React.FC<unknown>],
  ["summary-checklist", SummaryChecklistRenderer as unknown as React.FC<unknown>],
  ["static-note", StaticNoteRenderer as unknown as React.FC<unknown>],
  ["size-and-yield", SizeAndYieldRenderer as unknown as React.FC<unknown>],
  ["ranked-tier", RankedTierRenderer as unknown as React.FC<unknown>],
  ["branch-variant", BranchVariantRenderer as unknown as React.FC<unknown>],
  ["scaling-matrix", ScalingMatrixRenderer as unknown as React.FC<unknown>],
  ["option-tier-matrix", OptionTierMatrixRenderer as unknown as React.FC<unknown>],
  ["per-option-detail-card", PerOptionDetailCardRenderer as unknown as React.FC<unknown>],
  ["freeze-picker", FreezePickerRenderer as unknown as React.FC<unknown>],
  ["shopping-list-aggregator", ShoppingListAggregatorRenderer as unknown as React.FC<unknown>],
]);

// Phase 7 D-17 Layer 3: freeze the Map shell.
// Belt-and-suspenders alongside the ReadonlyMap type (Layer 1) and grep gate (Layer 4).
export const templateRenderers: ReadonlyMap<TemplateId, React.FC<unknown>> = Object.freeze(
  _renderers,
);
